export function normalizeBoardQuery(value) {
    return String(value ?? '')
        .trim()
        .toLocaleLowerCase('ru-RU')
        .split(/\s+/)
        .filter(Boolean)
        .join(' ');
}

function serializeTaskTags(tags) {
    if (!Array.isArray(tags)) return '';

    return tags
        .map((tag) => `${String(tag?.id ?? '')}:${String(tag?.title ?? '')}:${String(tag?.backgroundColor ?? '')}`)
        .join('|');
}

function areBoardTasksEqual(a, b) {
    if (a === b) return true;

    return !(
        String(a?.id ?? '') !== String(b?.id ?? '') ||
        String(a?.status ?? '') !== String(b?.status ?? '') ||
        String(a?.dateLastMess ?? '') !== String(b?.dateLastMess ?? '') ||
        String(a?.dateCreate ?? '') !== String(b?.dateCreate ?? '') ||
        String(a?.author ?? '') !== String(b?.author ?? '') ||
        String(a?.assignees ?? '') !== String(b?.assignees ?? '') ||
        String(a?.team ?? '') !== String(b?.team ?? '') ||
        String(a?.taskKind ?? '') !== String(b?.taskKind ?? '') ||
        String(a?.taskKindLabel ?? '') !== String(b?.taskKindLabel ?? '') ||
        String(a?.priority ?? '') !== String(b?.priority ?? '') ||
        String(a?.responseState ?? '') !== String(b?.responseState ?? '') ||
        String(a?.title ?? '') !== String(b?.title ?? '') ||
        String(a?.observers ?? '') !== String(b?.observers ?? '') ||
        String(a?.companyAddress ?? '') !== String(b?.companyAddress ?? '') ||
        String(a?.terminalNumber ?? '') !== String(b?.terminalNumber ?? '') ||
        String(a?.contactPhone ?? '') !== String(b?.contactPhone ?? '') ||
        String(a?.searchText ?? '') !== String(b?.searchText ?? '') ||
        serializeTaskTags(a?.tags) !== serializeTaskTags(b?.tags) ||
        String(a?.rating?.score ?? '') !== String(b?.rating?.score ?? '') ||
        String(a?.rating?.status ?? '') !== String(b?.rating?.status ?? '') ||
        Boolean(a?.terminalPaymentBlocked) !== Boolean(b?.terminalPaymentBlocked)
    );
}

export function areTaskListsEqual(prev, next) {
    if (prev === next) return true;
    if (!Array.isArray(prev) || !Array.isArray(next) || prev.length !== next.length) return false;

    return prev.every((task, index) => areBoardTasksEqual(task, next[index]));
}

export function areBoardQueriesEqual(left, right) {
    return normalizeBoardQuery(left) === normalizeBoardQuery(right);
}

// Poll responses contain fresh objects. Keep unchanged cards stable even when
// another task changes or moves to a different position in the response.
export function reconcileBoardTasks(previous, incoming) {
    if (areTaskListsEqual(previous, incoming)) return previous;

    const previousById = new Map(previous.map((task) => [String(task.id), task]));
    return incoming.map((task) => {
        const existing = previousById.get(String(task.id));
        return existing && areBoardTasksEqual(existing, task) ? existing : task;
    });
}

export function isBoardGlobalQueryTooShort(value, minimumLength = 3) {
    const query = normalizeBoardQuery(value).replace(/\s/g, '');
    return query !== '' && query.length < minimumLength;
}

export function createBoardTaskMatcher(filters = {}) {
    // Normalize filters once per list, and only inspect fields that are searched.
    const ipQuery = normalizeBoardQuery(filters.ip);
    const observerQuery = normalizeBoardQuery(filters.observer);
    const teamQuery = normalizeBoardQuery(filters.team);
    const taskIdQuery = String(filters.taskId ?? '').trim();

    return (task) => {
        const matchesIp =
            ipQuery === '' ||
            normalizeBoardQuery(task?.author).includes(ipQuery) ||
            normalizeBoardQuery(task?.assignees).includes(ipQuery);
        const matchesTaskId =
            taskIdQuery === '' ||
            (task?.id != null && String(task.id).includes(taskIdQuery));
        const matchesTeam =
            teamQuery === '' ||
            normalizeBoardQuery(task?.team) === teamQuery;
        const matchesObserver =
            observerQuery === '' ||
            normalizeBoardQuery(task?.observers).includes(observerQuery);

        // The global q search is intentionally absent here: /api/getTaskList applies it.
        return matchesIp && matchesTaskId && matchesTeam && matchesObserver;
    };
}

export function matchesBoardTask(task, filters = {}) {
    return createBoardTaskMatcher(filters)(task);
}
